/**
 * The storage and replay core of the offline sale queue.
 *
 * Deliberately free of `window`, `localStorage` and React: the same code runs
 * in the page and in the service worker, where a background sync can drain the
 * queue while no tab is open. UI concerns (status listeners, connectivity
 * reporting, simulated offline) stay in `offline-queue.ts`, which wraps this.
 *
 * Every rule about what a response means lives here, in one place:
 * - 2xx: the sale is recorded, delete it;
 * - 401/403: the session is gone, keep everything and say so;
 * - other 4xx: a real rejection, mark it for a human and stop retrying it;
 * - 5xx: the server is unwell, leave it pending and stop this run;
 * - no response at all: stop, the connection is still bad.
 *
 * A queued record is also stamped with the company (and user) that rung it up.
 * Replay only ever posts records belonging to the company signed in now: a
 * product id or credit-customer id is unique per company, so posting another
 * tenant's body under this token would address the wrong rows. Such records are
 * held, never deleted, for the account that owns them.
 */

const DB_NAME = 'stockwise-sales-queue';
const DB_VERSION = 2;
const STORE = 'sales';
const META_STORE = 'meta';
const TOKEN_KEY = 'token';
const IDENTITY_KEY = 'identity';

/** How many replays a single sale gets before it needs a human. */
export const MAX_REPLAY_ATTEMPTS = 5;

export interface QueuedSale {
  /** The `client_id` sent to the server. Also the record key. */
  id: string;
  endpoint: string;
  body: Record<string, unknown>;
  createdAt: number;
  /**
   * When the sale happened on this till, in epoch ms. Kept apart from
   * `createdAt` (when it was queued) because the server dates the order by the
   * sale, not by the replay. Absent on rows queued before this existed.
   */
  capturedAt?: number;
  /**
   * The number printed on the customer's slip while the sale is unsynced.
   * Built from the device and a local counter, so two tills cannot mint the
   * same one. The server assigns the real order number on replay.
   */
  receipt?: string;
  /** Total shown on that slip, from the till's own arithmetic. Display only. */
  total?: number;
  /**
   * What kind of record this is. The queue carries more than sales now, and the
   * UI must not call a repayment a sale or print a sales slip for it. Absent on
   * rows queued before this existed, which are all sales.
   */
  kind?: 'sale' | 'credit_payment';
  /**
   * The tenant that rung this up, and the staff member whose session queued it.
   * Absent on rows written before scoping existed (or with no cached session to
   * read it from); those replay under whatever session is signed in now.
   */
  companyId?: number;
  userId?: number | null;
  /**
   * Set when this record cannot be sent by the session that is replaying it
   * (another company). Held, not failed: the right account can still send it.
   */
  heldReason?: string | null;
  attempts: number;
  /**
   * Set only after the record has spent its whole replay budget on rejections
   * (or on one rejection, when that was its last attempt). Until then a 4xx
   * leaves it pending so the next flush can try again.
   */
  lastError: string | null;
}

/** Who is replaying: the company is the tenant boundary, the user only says
 * which staff member owns the records. */
export interface QueueIdentity {
  companyId: number;
  userId: number | null;
}

export interface ReplayOutcome {
  /** Sales the server accepted and that are now gone from the queue. */
  replayed: number;
  /** Sales still waiting (pending or failed) when the run ended. */
  remaining: number;
  /** A 401/403 stopped the run: the app needs a fresh session to finish. */
  needsSignIn: boolean;
  /** The last request got no response at all. */
  unreachable: boolean;
  /** Records held because they belong to another company. Not sent, not lost. */
  held: number;
}

// ── IndexedDB plumbing ───────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
      // Added in v2: the worker cannot read localStorage, so the bearer token it
      // needs to replay is kept beside the queue instead.
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })

  return dbPromise
}

async function runRequest<T>(
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb()
  if (!db) return null

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(storeName, mode)
      const request = action(transaction.objectStore(storeName))
      request.onsuccess = () => resolve(request.result ?? null)
      request.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

export function openQueueDatabase(): Promise<IDBDatabase | null> {
  return openDb()
}

// ── Queue operations ─────────────────────────────────────────────────────────

export async function listSales(): Promise<QueuedSale[]> {
  const rows = await runRequest<QueuedSale[]>(STORE, 'readonly', (store) => store.getAll() as IDBRequest<QueuedSale[]>)
  return (rows ?? []).sort((a, b) => a.createdAt - b.createdAt)
}

export async function putSale(sale: QueuedSale): Promise<void> {
  await runRequest(STORE, 'readwrite', (store) => store.put(sale))
}

export async function deleteSale(id: string): Promise<void> {
  await runRequest(STORE, 'readwrite', (store) => store.delete(id))
}

/** Store the bearer token for the service worker. Storage only — never logged. */
export async function setQueueToken(token: string | null): Promise<void> {
  await runRequest(META_STORE, 'readwrite', (store) =>
    token ? store.put(token, TOKEN_KEY) : store.delete(TOKEN_KEY),
  )
}

export async function getQueueToken(): Promise<string | null> {
  const value = await runRequest<unknown>(META_STORE, 'readonly', (store) => store.get(TOKEN_KEY))
  return typeof value === 'string' && value.length > 0 ? value : null
}

/** The identity a replay must match. Stored beside the token because the
 * service worker cannot read localStorage. */
export async function setQueueIdentity(identity: QueueIdentity | null): Promise<void> {
  await runRequest(META_STORE, 'readwrite', (store) =>
    identity ? store.put(identity, IDENTITY_KEY) : store.delete(IDENTITY_KEY),
  )
}

export async function getQueueIdentity(): Promise<QueueIdentity | null> {
  const value = await runRequest<unknown>(META_STORE, 'readonly', (store) => store.get(IDENTITY_KEY))
  if (!value || typeof value !== 'object') return null
  const companyId = Number((value as QueueIdentity).companyId)
  if (!Number.isFinite(companyId)) return null
  const userId = (value as QueueIdentity).userId
  return { companyId, userId: typeof userId === 'number' ? userId : null }
}

/** What a queued endpoint represents, for labels and printing. */
export function queuedKindFor(endpoint: string): 'sale' | 'credit_payment' {
  return endpoint.includes('/credit/payments') ? 'credit_payment' : 'sale'
}

/** A UUID that also works where `crypto.randomUUID` is unavailable. */
export function newSaleId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: 16 random bytes, hex. Only used on older/insecure contexts.
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ── Replay ───────────────────────────────────────────────────────────────────

export interface ReplayOptions {
  token: string | null
  /**
   * The company/user signed in now. A record stamped for another company is
   * held instead of posted, because its body addresses that company's rows.
   */
  identity?: QueueIdentity | null
  /**
   * Aborts the run between records, and the in-flight request itself. Logging
   * out cancels a replay that is already under way, so the session that is
   * ending cannot keep posting sales.
   */
  signal?: AbortSignal
  /** Injected so the page and the worker each use their own fetch. */
  fetchImpl: (input: string, init: RequestInit) => Promise<Response>
}

/**
 * Replay every pending sale, oldest first.
 *
 * Serialised by the caller (the page takes a Web Lock; a background sync event
 * is already exclusive per registration).
 */
export async function replayPendingSales({
  token,
  identity = null,
  signal,
  fetchImpl,
}: ReplayOptions): Promise<ReplayOutcome> {
  const pending = (await listSales()).filter((sale) => !sale.lastError)
  let replayed = 0
  let needsSignIn = false
  let unreachable = false
  let held = 0

  for (const sale of pending) {
    if (signal?.aborted) break
    // Tenant gate. Without this, a sale rung up in company A and replayed after
    // someone signs in to company B would post A's product ids and credit
    // customer ids in B's request — ids that are only unique inside a company.
    if (typeof sale.companyId === 'number' && sale.companyId !== identity?.companyId) {
      held += 1
      const heldReason = 'Queued by a different company — sign in as that company to send it'
      if (sale.heldReason !== heldReason) await putSale({ ...sale, heldReason })
      continue
    }
    // The owning account is back: clear the notice before sending.
    if (sale.heldReason) await putSale({ ...sale, heldReason: null })

    let response: Response
    try {
      response = await fetchImpl(sale.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // The server relaxes its stock check for this flag: the sale already
        // happened in the shop, so a shortage must not discard it.
        // `captured_at` dates the order server-side, so a replay keeps the day
        // it was rung up. Rows queued before that field existed fall back to
        // when they were queued.
        body: JSON.stringify({
          ...sale.body,
          captured_at:
            sale.body.captured_at ?? new Date(sale.capturedAt ?? sale.createdAt).toISOString(),
          offline_replay: true,
        }),
        signal,
      })
    } catch {
      // A deliberate stop (logout) is not a network failure: everything stays
      // queued and the next session continues, without telling the app the
      // server became unreachable.
      if (signal?.aborted) break
      // Still offline or the connection dropped: stop, keep everything queued.
      unreachable = true
      break
    }

    if (response.ok) {
      await deleteSale(sale.id)
      replayed += 1
      continue
    }

    // The session ended: the sale is real revenue, so it stays queued.
    if (response.status === 401 || response.status === 403) {
      needsSignIn = true
      break
    }

    // A 5xx is the server failing, not rejecting the sale. It must stay pending:
    // recording it as failed would sideline real revenue until a person noticed
    // and retried it by hand.
    if (response.status >= 500) break

    let detail = `Server rejected the sale (${response.status})`
    try {
      const payload = await response.json()
      if (payload?.detail) detail = String(payload.detail)
    } catch {
      /* non-JSON error body — keep the status-based message */
    }

    // A 4xx is not always final. A stock check that lost a race, a row another
    // till is still writing, a rate limit — all clear up on their own. The sale
    // keeps its place in the queue until it has spent its whole budget, and only
    // then does it become a failure a human has to look at.
    const attempts = sale.attempts + 1
    const exhausted = attempts >= MAX_REPLAY_ATTEMPTS
    await putSale({
      ...sale,
      attempts,
      lastError: exhausted ? `${detail} (gave up after ${attempts} attempts)` : null,
    })
  }

  return {
    replayed,
    remaining: (await listSales()).length,
    needsSignIn,
    unreachable,
    held,
  }
}
